import { useState } from "react";
import { supabase } from "@/lib/supabase";
import { useFileUploader } from "@/components/features/FileUploader";
import { UploadFileResult } from "@/lib/supabase";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/features/auth/context/AuthContext";
import { useDocumentTypes } from "@/hooks/useDocumentTypes";

interface UseDocumentActionsProps {
  application_id?: string;
  talent_id: string;
  isTalentProfile: boolean;
  onSuccess?: () => Promise<void>;
}

export interface UseDocumentActionsReturn {
  saving: boolean;
  handleDocumentUpload: (
    file: File | null,
    documentTypeId: number | null
  ) => Promise<boolean>;
  handleDocumentUpdate: (
    documentId: number,
    updates: { document_type_id?: number | null; document_status?: string }
  ) => Promise<boolean>;
  handleDeleteDocument: (documentId: number) => Promise<boolean>;
}

export function useDocumentActions({
  application_id,
  talent_id,
  isTalentProfile,
  onSuccess
}: UseDocumentActionsProps) {
  const [saving, setSaving] = useState(false);
  const { uploadTalentProfileDocument, uploadApplicationDocument } =
    useFileUploader();
  const { toast } = useToast();
  const { t } = useTranslation();
  const { profile } = useAuth();
  const { data: documentTypes = [] } = useDocumentTypes();

  const handleDocumentUpload = async (
    file: File | null,
    documentTypeId: number | null
  ) => {
    if (!file) {
      toast({
        title: t("common:status.error"),
        description: t("common:documents.errors.select_file_and_fill_fields"),
        variant: "destructive"
      });
      return false;
    }

    setSaving(true);
    const bucket = !isTalentProfile ? "applications" : "profiles";

    try {
      const documentType = documentTypes.find((d) => d.id === documentTypeId);
      if (!documentType) {
        toast({
          title: t("common:status.error"),
          description: t("common:documents.errors.upload_failed"),
          variant: "destructive"
        });
        throw new Error("Document type not found");
      }

      const valuesToInsertInDocumentsTable: Record<string, any> = {
        uploaded_by: profile.id,
        talent_id
      };

      if (bucket === "applications") {
        valuesToInsertInDocumentsTable.application_id = application_id;
      } else {
        valuesToInsertInDocumentsTable.talent_id = talent_id;
      }

      let result: UploadFileResult;
      if (isTalentProfile) {
        result = await uploadTalentProfileDocument(
          file,
          documentType,
          talent_id!,
          {
            values: valuesToInsertInDocumentsTable
          }
        );
      } else {
        result = await uploadApplicationDocument(
          file,
          documentType,
          application_id!,
          {
            values: valuesToInsertInDocumentsTable
          }
        );
      }

      if (!result.success) throw result.error;

      if (onSuccess) await onSuccess();

      return true;
    } catch (error) {
      console.error("Error uploading document:", error);
      toast({
        title: t("common:status.error"),
        description: t("common:documents.errors.upload_failed"),
        variant: "destructive"
      });
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleDocumentUpdate = async (
    documentId: number,
    updates: { document_type_id?: number | null; document_status?: string }
  ) => {
    if (!documentId) return false;

    setSaving(true);
    try {
      const { error } = await supabase
        .from("documents")
        .update({
          ...(updates.document_type_id !== undefined && {
            document_type_id: updates.document_type_id
          }),
          ...(updates.document_status !== undefined && {
            document_status: updates.document_status
          }),
          updated_at: new Date().toISOString()
        })
        .eq("id", documentId);

      if (error) throw error;

      toast({
        title: t("common:status.success"),
        description: t("common:documents.success.updated")
      });

      if (onSuccess) await onSuccess();

      return true;
    } catch (error) {
      console.error("Error updating document:", error);
      toast({
        title: t("common:status.error"),
        description: t("common:documents.errors.update_failed"),
        variant: "destructive"
      });
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDocument = async (documentId: number) => {
    if (!documentId) return false;

    if (!window.confirm(t("common:documents.confirm_delete"))) {
      return false;
    }

    try {
      const { data: documentData, error: fetchError } = await supabase
        .from("documents")
        .select("file_path")
        .eq("id", documentId)
        .single();

      if (fetchError) throw fetchError;

      const { error } = await supabase
        .from("documents")
        .delete()
        .eq("id", documentId);

      if (error) throw error;

      if (documentData?.file_path) {
        const { error: storageError } = await supabase.storage
          .from("documents")
          .remove([documentData.file_path]);

        if (storageError) {
          console.error("Error deleting file from storage:", storageError);
        }
      }

      toast({
        title: t("common:status.success"),
        description: t("common:documents.success.deleted")
      });

      if (onSuccess) await onSuccess();

      return true;
    } catch (error) {
      console.error("Error deleting document:", error);
      toast({
        title: t("common:status.error"),
        description: t("common:documents.errors.delete_failed"),
        variant: "destructive"
      });
      return false;
    }
  };

  return {
    saving,
    handleDocumentUpload,
    handleDocumentUpdate,
    handleDeleteDocument
  };
}
